import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "bootstrap/dist/css/bootstrap.min.css";
import "./Login.css";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import { FlagTH, FlagUS } from "../components/FlagIcon";
import API from "../config";

function Login() {
  const navigate = useNavigate();
  const { lang, toggle } = useLanguage();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const role  = localStorage.getItem("role");
    if (!token) return;
    try {
      const { exp } = jwtDecode(token);
      if (exp * 1000 > Date.now()) {
        if (role === "admin") navigate("/admin", { replace: true });
        else if (role === "staff") navigate("/calendar", { replace: true });
        else navigate("/rooms", { replace: true });
      } else {
        localStorage.removeItem("token");
        localStorage.removeItem("role");
      }
    } catch {
      localStorage.removeItem("token");
      localStorage.removeItem("role");
    }
  }, [navigate]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleLogin = async () => {
    setMessage("");
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password })
      });

      const data = await res.json();

      if (!res.ok) {
        setMessage(data.message || "Login failed");
        setLoading(false);
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.setItem("role", data.role || "user");

      if (data.role === "admin") {
        navigate("/admin");
        return;
      }
      if (data.role === "staff") {
        navigate("/calendar");   // ⭐ staff ไปหน้า calendar
        return;
      }

      const profileRes = await fetch(`${API}/api/profile-status`, {
        headers: { "Authorization": `Bearer ${data.token}` }
      });

      if (!profileRes.ok) {
        setMessage("Cannot load profile status");
        setLoading(false);
        return;
      }

      // ✅ ลบ profileData ที่ไม่ได้ใช้ออก
      await profileRes.json();
      navigate("/rooms");

    } catch (err) {
      console.error(err);
      setMessage("Cannot connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    window.location.href = API + "/api/auth/google";
  };

  return (
    <div className="login-bg">

      {/* LANG TOGGLE */}
      <button
        onClick={toggle}
        style={{ position: "fixed", top: 16, right: 16, background: "none", border: "none", cursor: "pointer", zIndex: 100 }}
        title={lang === "th" ? "Switch to English" : "เปลี่ยนเป็นภาษาไทย"}
      >
        {lang === "th" ? <FlagUS size={28} /> : <FlagTH size={28} />}
      </button>

      {/* TOP LOGO */}
      <div className="text-center mb-3">
        <img src="/logo.png" width="90" alt="logo" />
      </div>

      {/* GLASS CARD */}
      <div className="glass-card">
        <h4 className="text-center mb-4">{t(lang,"signIn")}</h4>

        {/* SOCIAL LOGIN BUTTONS */}
        <div className="social-login-group">
          <button className="social-glass-btn google-glass-btn" onClick={handleGoogleLogin}>
            <svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {t(lang,"continueGoogle")}
          </button>
        </div>

        {/* DIVIDER */}
        <div className="glass-divider">
          <span>{t(lang,"orEmail")}</span>
        </div>

        {/* EMAIL */}
        <div className="input-group-glass">
          <label>{t(lang,"emailLabel")}</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />
        </div>

        {/* PASSWORD */}
        <div className="input-group-glass">
          <label>{t(lang,"passwordLabel")}</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleLogin()}
          />
        </div>

        <button className="glass-btn-login" onClick={handleLogin} disabled={loading}>
          {loading ? t(lang,"signingIn") : t(lang,"logIn")}
        </button>

        {message && (
          <p className="text-danger text-center mt-2">{message}</p>
        )}

        <p
          className="text-center forget-text"
          onClick={() => navigate("/forgot-password")}
        >
          {t(lang,"forgotPass")}
        </p>
      </div>

      {/* CREATE ACCOUNT */}
      <div className="bottom-btn-wrapper">
        <Link to="/register" className="glass-btn outline-btn">
          {t(lang,"createAccount")}
        </Link>
      </div>

    </div>
  );
}

export default Login;